import React, { FC, useRef } from 'react';
import { ContentContext, LabelContentContext } from './contexts';
import { persistStore } from 'redux-persist';
import type { Persistor } from 'redux-persist';
import { Provider } from 'react-redux';
import ReduxStore from '@trip-ux/redux-store';
import { PersistGate } from 'redux-persist/integration/react';
import { Progress } from '../Progress';

// Module-level map of persistors keyed by persistConfig.key.
// Using a module-scoped singleton avoids mutating `window`/`global`,
// is easier to reset in tests, and keeps dedupe behavior in the browser.
const PERSISTORS: Record<string, Persistor | undefined> = {};

// Module-level map of stores keyed by persistConfig.key. This matches the
// persistor dedupe behavior and provides a clear singleton per client page
// load. We intentionally do not reuse these on the server to avoid sharing
// state between SSR requests.
const STORES: Record<string, ReturnType<typeof ReduxStore.createStore> | undefined> = {};

export const ContentProvider: FC<{ init; children?: React.ReactNode }> = ({ children, init }) => (
  <ContentContext.Provider value={init}>{children}</ContentContext.Provider>
);

interface ReduxProviderProps {
  //NOSONAR
  init: any;
  //NOSONAR
  persistConfig: any;
  loadProgress?: boolean;
  children?: React.ReactNode;
}

export const ReduxProvider: FC<ReduxProviderProps> = ({
  children,
  init,
  persistConfig,
  loadProgress = false
}) => {
  // Ensure the store is created once per client page load (per persist key),
  // but create a fresh store on the server to avoid sharing state across
  // SSR requests. Calling `persistStore` multiple times (for the same store)
  // can cause duplicate persist/REHYDRATE actions which shows up as
  // rehydrate running twice in the logs.
  const isClient = typeof window !== 'undefined';

  let store: ReturnType<typeof ReduxStore.createStore>;
  if (isClient) {
    const storeKey = persistConfig?.key || 'default';
    if (!STORES[storeKey]) {
      STORES[storeKey] = ReduxStore.createStore(init, persistConfig);
    }
    store = STORES[storeKey]!;
  } else {
    // On the server, always create a new store so requests remain isolated.
    store = ReduxStore.createStore(init, persistConfig);
  }
  const persisterRef = useRef<Persistor | null>(null);

  // Only create/use a persistor in the browser. On the server we should not
  // reuse a single persistor across requests — so we skip PersistGate during
  // SSR and render children directly.
  if (isClient && !persisterRef.current) {
  const persistKey = persistConfig?.key || 'default';
    if (!PERSISTORS[persistKey]) {
      PERSISTORS[persistKey] = persistStore(store);
    }
    persisterRef.current = PERSISTORS[persistKey];
  }

  const persister: Persistor | null = persisterRef.current;

  // If we're rendering on the server, or the persistor isn't available for
  // some reason, render without PersistGate to avoid server-side sharing and
  // rendering issues.
  if (!isClient || !persister) {
    return <Provider store={store}>{children}</Provider>;
  }

  return (
    <Provider store={store}>
      <PersistGate loading={loadProgress ? <Progress backdrop isFloating /> : <div />} persistor={persister}>
        {children}
      </PersistGate>
    </Provider>
  );
};

export const LabelContentProvider: FC<{ init; children?: React.ReactNode }> = ({ children, init }) => (
  <LabelContentContext.Provider value={init}>{children}</LabelContentContext.Provider>
);
